import { PDFCheckBox, PDFDropdown, PDFRadioGroup, PDFTextField } from "pdf-lib";
import React from "react";
import { processField } from "./utils";
import TextFormField from "./TextFormField";
import FieldFocusRing, { focusOnMouseDown } from "./FieldFocusRing";

const FormField = ({ pages, field, pdfScale, pdfWidth, ruledLayout = null }) => {
    const fieldData = processField(pages, field, pdfScale, pdfWidth, ruledLayout);

    if (field instanceof PDFTextField) {
        if (field.isCombed()) {
            return fieldData.map((data) => {
                const { pageIndex } = data[0];
                return {
                    pageIndex,
                    component: ({ valueGetter, onChange, readOnly, overflowPolicyGetter }) => (
                        <TextFormField
                            fieldData={data}
                            isCombed
                            name={field.getName()}
                            value={valueGetter(field.getName())}
                            onChange={onChange}
                            readOnly={readOnly}
                            overflowPolicy={overflowPolicyGetter?.(field.getName())}
                        />
                    ),
                };
            });
        }
        return fieldData.map((data) => {
            const { pageIndex } = data;
            return {
                pageIndex,
                component: ({ valueGetter, onChange, readOnly, overflowPolicyGetter }) => (
                    <TextFormField
                        fieldData={data}
                        isCombed={field.isCombed()}
                        name={field.getName()}
                        value={valueGetter(field.getName())}
                        onChange={onChange}
                        readOnly={readOnly}
                        overflowPolicy={overflowPolicyGetter?.(field.getName())}
                    />
                ),
            };
        });
    }

    if (field instanceof PDFDropdown) {
        return fieldData.map((data) => {
            const { cssCoords, pageIndex } = data;
            const inputStyle = {
                position: "absolute",
                top: cssCoords.top,
                left: cssCoords.left,
                width: cssCoords.width,
                height: cssCoords.height,
            };
            return {
                pageIndex,
                component: ({ valueGetter, onChange, readOnly }) => (
                    <FieldFocusRing cssCoords={cssCoords}>
                        <select
                            style={inputStyle}
                            name={field.getName()}
                            value={valueGetter(field.getName())}
                            onChange={(e) => onChange(field.getName(), e.target.value)}
                            onMouseDown={focusOnMouseDown}
                            disabled={readOnly}
                            data-field-name={field.getName()}
                        >
                            {field.getOptions().map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </FieldFocusRing>
                ),
            };
        });
    }

    if (field instanceof PDFCheckBox) {
        return fieldData.map((data) => {
            const { cssCoords, pageIndex } = data;
            const inputStyle = {
                position: "absolute",
                top: cssCoords.top,
                left: cssCoords.left,
                width: cssCoords.width,
                height: cssCoords.height,
            };
            return {
                pageIndex,
                component: ({ valueGetter, onChange, readOnly }) => {
                    const rawValue = valueGetter(field.getName());
                    // El valor puede llegar como booleano (tras un toggle en sesión) o como
                    // el string "true"/"false" persistido. Boolean("false") es truthy, así que
                    // hay que comparar explícitamente contra "true".
                    const isChecked = rawValue === true || String(rawValue).toLowerCase() === "true";
                    return (
                        <FieldFocusRing cssCoords={cssCoords}>
                            <input
                                type="checkbox"
                                style={inputStyle}
                                name={field.getName()}
                                checked={isChecked}
                                onChange={(e) => onChange(field.getName(), e.target.checked)}
                                onMouseDown={focusOnMouseDown}
                                readOnly={readOnly}
                                disabled={readOnly}
                                data-field-name={field.getName()}
                            />
                        </FieldFocusRing>
                    );
                },
            };
        });
    }

    if (field instanceof PDFRadioGroup) {
        // Un radio group tiene un widget por opcion y getOptions() los devuelve en
        // ese mismo orden, asi que el indice del widget identifica a la opcion.
        const options = field.getOptions();
        return fieldData.map((data, optionIndex) => {
            const { cssCoords, pageIndex } = data;
            const option = options[optionIndex];
            const inputStyle = {
                position: "absolute",
                top: cssCoords.top,
                left: cssCoords.left,
                width: cssCoords.width,
                height: cssCoords.height,
            };
            return {
                pageIndex,
                component: ({ valueGetter, onChange, readOnly }) => (
                    <FieldFocusRing cssCoords={cssCoords}>
                        <input
                            type="radio"
                            style={inputStyle}
                            name={field.getName()}
                            value={option}
                            checked={valueGetter(field.getName()) === option}
                            onChange={() => onChange(field.getName(), option)}
                            onMouseDown={focusOnMouseDown}
                            readOnly={readOnly}
                            disabled={readOnly || option === undefined}
                            data-field-name={field.getName()}
                        />
                    </FieldFocusRing>
                ),
            };
        });
    }

    // Tipos sin equivalente editable en el overlay HTML (firmas, botones, listas
    // multiseleccion): no se pintan, pero hay que devolver una lista vacia porque
    // quien llama itera el resultado sin comprobarlo.
    return [];
};

export default FormField;
